(() => {
  "use strict";
  // Davines professional litre sizes are sold by direct link only.
  const njPrivateDavinesLiter = p => {
    const variants = Array.isArray(p?.variants) ? p.variants : (p?.variants?.nodes || []);
    const text = [p?.title, p?.handle, ...variants.map(v => v?.title || '')]
      .join(' ').replace(/[-_]/g, ' ');
    return /davines/i.test(String(p?.vendor || '') + ' ' + text) &&
      /(?:^|[^\d])(?:1\s*(?:l|litros?)\b|1[.,]000\s*ml\b|1000\s*ml\b|litros?\b)/i.test(text);
  };
  // Do not surface gifts, zero-price items or salon-only services as purchasable cart suggestions.
  const njSellableSuggestion = p => {
    if (!p || !p.available || !p.handle || !Number(p.price) || njPrivateDavinesLiter(p)) return false;
    const title = String(p.title || "").trim();
    const vendor = String(p.vendor || "").trim();
    const type = String(p.type || "").trim();
    if (/^(?:🎁|muestras?\b|samples?\b)/i.test(title) ||
        /formato mini regalo|100\s*%\s*off/i.test(title) ||
        /^(?:incentivo compra|noelia jim[eé]nez shop)$/i.test(vendor) ||
        /(?:^|\b)(?:servicios?|bonos?|manicura|pedicura|indiba|microneedling|presoterapia|tratamiento facial)(?:\b|$)/i.test(type)) return false;
    const variants = Array.isArray(p.variants) ? p.variants : [];
    if (variants.length && variants.every(v => v.requires_shipping === false)) return false;
    return true;
  };
  const selector = "[data-nj-cart-crosssell]";
  const localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const root = localeRoot.endsWith("/") ? localeRoot : localeRoot + "/";
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "EUR";
  const formatter = new Intl.NumberFormat("es-ES", { style: "currency", currency });
  const curated = window.NJProductPairs || {};
  const getIds = (str) => [...new Set((str || "").split(",").map(v => v.trim()).filter(v => /^\d+$/.test(v)))];

  function safeUrl(input, fallback) {
    try {
      const u = new URL(input || fallback, window.location.origin);
      return u.origin === window.location.origin ? u.href : null;
    } catch (_) { return null; }
  }

  function makeCard(product, container) {
    if (!njSellableSuggestion(product)) return;
    const href = safeUrl(product.url, root + "products/" + encodeURIComponent(product.handle || ""));
    if (!href) return;
    const card = document.createElement("article");
    card.className = "nj-cart-rec";

    const imageLink = document.createElement("a");
    imageLink.href = href;
    imageLink.setAttribute("aria-label", "Ver " + product.title);
    const src = product.featured_image || (product.images && product.images[0]);
    if (src && (/^https?:\/\//.test(src) || src.startsWith("//") || src.startsWith("/"))) {
      const img = document.createElement("img");
      img.src = src;
      img.alt = product.title;
      img.className = "nj-cart-rec-image";
      img.width = 56;
      img.height = 66;
      img.loading = "lazy";
      imageLink.append(img);
    }

    const details = document.createElement("div");
    details.className = "nj-cart-rec-details";
    const link = document.createElement("a");
    link.href = href;
    link.textContent = product.title;
    details.append(link);
    const price = document.createElement("span");
    price.className = "nj-cart-rec-price";
    price.textContent = formatter.format((Number(product.price) || 0) / 100);
    details.append(price);

    const variants = (product.variants || []).filter(v => v && v.available);
    const btn = document.createElement(variants.length === 1 && product.variants.length === 1 ? "button" : "a");
    btn.className = "nj-cart-rec-action";
    if (btn.tagName === "A") {
      btn.href = href;
      btn.textContent = "Ver";
    } else {
      btn.type = "button";
      btn.textContent = "Añadir";
      btn.addEventListener("click", async () => {
        btn.disabled = true;
        btn.textContent = "Añadiendo…";
        try {
          const r = await fetch(root + "cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ id: variants[0].id, quantity: 1, properties: { "_nj_origen": "venta_cruzada_carrito" } })
          });
          if (!r.ok) throw new Error("No se pudo añadir");
          const saved = await r.json().catch(() => null);
          const njOriginConfirmed = saved?.properties?._nj_origen === "venta_cruzada_carrito";
          btn.textContent = "Añadido ✓";
          document.dispatchEvent(new CustomEvent("nj:analytics", {detail:{event:"nj_cart_crosssell_add_success",nj_page:location.pathname,nj_variant_id:String(variants[0].id),nj_origin_confirmed:njOriginConfirmed,nj_source:"cart_crosssell"}}));
          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
          // Fallback only if the theme has not replaced this drawer after the refresh event.
          window.setTimeout(() => { if (container.isConnected) window.location.reload(); }, 1200);
        } catch (_) {
          btn.disabled = false;
          btn.textContent = "Reintentar";
        }
      });
    }
    card.append(imageLink, details, btn);
    container.append(card);
  }

  async function populate(el) {
    if (el.dataset.njState) return;
    el.dataset.njState = "loading";
    const ids = getIds(el.dataset.products).slice(0, 3);
    const inCart = new Set(getIds(el.dataset.inCart));
    if (!ids.length) { el.dataset.njState = "done"; return; }
    try {
      const responses = await Promise.all(ids.map(async id => {
        try {
          const res = await fetch(root + "recommendations/products.json?product_id=" +
            encodeURIComponent(id) + "&intent=complementary&limit=5",
            { headers: { "Accept": "application/json" } });
          if (!res.ok) return [];
          const data = await res.json();
          return Array.isArray(data.products) ? data.products : [];
        } catch (_) { return []; }
      }));
      if (!el.isConnected) return;
      const seen = new Set();
      const choices = [];
      for (const list of responses) {
        for (const item of list) {
          if (!njSellableSuggestion(item) || inCart.has(String(item.id)) || seen.has(String(item.id))) continue;
          seen.add(String(item.id));
          choices.push(item);
          if (choices.length >= 2) break;
        }
        if (choices.length >= 2) break;
      }
      // Complementos editoriales: van antes de sugerencias similares y se verifican en tiempo real.
      if (choices.length < 2) {
        const sourceHandles = [...new Set((el.dataset.handles || "").split(",").map(h => h.trim()).filter(Boolean))].slice(0, 3);
        const handles = [...new Set(sourceHandles.flatMap(h => curated[h] || []))].slice(0, 6);
        const candidates = await Promise.all(handles.map(async h => {
          try {
            const res = await fetch(root + "products/" + encodeURIComponent(h) + ".js", { headers: { "Accept": "application/json" } });
            if (!res.ok) return null;
            return await res.json();
          } catch (_) { return null; }
        }));
        for (const item of candidates) {
          if (!njSellableSuggestion(item) || inCart.has(String(item.id)) || seen.has(String(item.id))) continue;
          seen.add(String(item.id));
          choices.push(item);
          if (choices.length >= 2) break;
        }
      }
      let usedRelatedFallback = false;
      if (!choices.length) {
        const relatedResponses = await Promise.all(ids.map(async id => {
          try {
            const res = await fetch(root + "recommendations/products.json?product_id=" +
              encodeURIComponent(id) + "&intent=related&limit=10",
              { headers: { "Accept": "application/json" } });
            if (!res.ok) return [];
            const data = await res.json();
            return Array.isArray(data.products) ? data.products : [];
          } catch (_) { return []; }
        }));
        for (const related of relatedResponses) {
          for (const item of related) {
            if (!njSellableSuggestion(item) || inCart.has(String(item.id)) || seen.has(String(item.id))) continue;
            seen.add(String(item.id));
            choices.push(item);
            usedRelatedFallback = true;
            if (choices.length >= 2) break;
          }
          if (choices.length >= 2) break;
        }
      }

      if (choices.length) {
        if (usedRelatedFallback) {
          const headline = el.querySelector(".nj-cart-recs-header p");
          const description = el.querySelector(".nj-cart-recs-header span");
          if (headline) headline.textContent = "TAMBIÉN TE PUEDE INTERESAR";
          if (description) description.textContent = "Otras sugerencias relacionadas con tu selección";
        }
        const listEl = el.querySelector("[data-nj-cart-recs-list]");
        if (listEl) {
          // En móvil, una sola recomendación deja más espacio a los artículos y al pago.
          const visibleLimit = window.matchMedia && window.matchMedia("(max-width: 640px)").matches ? 1 : 2;
          choices.slice(0, visibleLimit).forEach(product => makeCard(product, listEl));
          if (listEl.children.length) el.hidden = false;
        }
      }
    } finally {
      el.dataset.njState = "done";
    }
  }

  let queued = false;
  function scan() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      document.querySelectorAll(selector).forEach(el => {
        if (!el.dataset.njState) populate(el).catch(() => { el.dataset.njState = "done"; });
      });
    });
  }

  function start() {
    scan();
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();